import { useCallback, useEffect, useState } from "react";
import { useMap } from "react-map-gl/mapbox";
import type { MapMouseEvent } from "mapbox-gl";
import { ADVENTURE_MAP_PLACES_LAYER_ID, PLACES_LAYER_ID, SELECTED_PLACE_LAYER_ID } from "../../../lib/mapConstants";

/** Layer IDs to exclude from trail click handling (our own place layers) */
const EXCLUDED_LAYER_IDS = [PLACES_LAYER_ID, ADVENTURE_MAP_PLACES_LAYER_ID, SELECTED_PLACE_LAYER_ID];

/**
 * Mapbox style layer IDs for trails. These must match the layer IDs in the Mapbox Studio style.
 * - mtb-hiking-trails-hitarea: Invisible wider layer for easier clicking
 * - mtb-hiking-trails: Visible trail line layer
 * - mtb-hiking-trails-labels: Trail name labels
 *
 * If layers don't exist in the style, they're gracefully filtered out (see findTrailLayers).
 */
const TRAIL_LAYER_IDS = ["mtb-hiking-trails-hitarea", "mtb-hiking-trails", "mtb-hiking-trails-labels"];

export interface TrailClickEvent {
  /** Name of the trail (may be null if unnamed in OSM) */
  name: string | null;
  /** Coordinates where the user clicked (proposed trailhead location) */
  coordinates: [number, number];
  /** Suggested place type */
  suggestedType: "hiking";
}

interface MapboxTrailsLayerProps {
  /** Whether trail click handling is enabled */
  enabled?: boolean;
  /** Called when a trail is clicked */
  onClick?: (event: TrailClickEvent) => void;
  /** Called when a trail is right-clicked / long-pressed */
  onContextMenu?: (event: TrailClickEvent) => void;
}

/**
 * Intercepts clicks on Mapbox trail layers (hiking paths, MTB trails)
 * and extracts trail information for the "add place" flow.
 *
 * This is a virtual layer that doesn't render anything - it just
 * attaches click handlers to the trail layers in the Mapbox style.
 */
export function MapboxTrailsLayer({ enabled = true, onClick, onContextMenu }: MapboxTrailsLayerProps) {
  const { current: map } = useMap();
  const [availableLayers, setAvailableLayers] = useState<string[]>([]);

  // Find and show trail layers (hidden by default in Mapbox Studio)
  useEffect(() => {
    if (!map) return;
    const mapInstance = map.getMap();

    const showTrailLayers = () => {
      const layers = TRAIL_LAYER_IDS.filter((id) => mapInstance.getLayer(id));
      layers.forEach((id) => mapInstance.setLayoutProperty(id, "visibility", "visible"));
      setAvailableLayers(layers);
    };

    if (mapInstance.isStyleLoaded()) {
      showTrailLayers();
    } else {
      mapInstance.once("styledata", showTrailLayers);
    }

    return () => {
      TRAIL_LAYER_IDS.forEach((id) => {
        if (mapInstance.getLayer(id)) {
          mapInstance.setLayoutProperty(id, "visibility", "none");
        }
      });
    };
  }, [map]);

  // Extract trail info from a map event
  const extractTrailInfo = useCallback(
    (e: MapMouseEvent): TrailClickEvent | null => {
      if (!enabled || !map) return null;

      // Skip if we clicked on our own place layers
      const excludedFeatures = map.queryRenderedFeatures(e.point, {
        layers: EXCLUDED_LAYER_IDS.filter((id) => map.getLayer(id)),
      });
      if (excludedFeatures?.length) return null;

      const feature = e.features?.[0];
      if (!feature) return null;

      // Use English name if available, fallback to default, or null if unnamed
      const name = (feature.properties?.name_en || feature.properties?.name || null) as string | null;

      // Use click coordinates as proposed trailhead (not the line geometry)
      const coordinates: [number, number] = [e.lngLat.lng, e.lngLat.lat];

      return { name, coordinates, suggestedType: "hiking" };
    },
    [map, enabled],
  );

  // Click handler
  const handleClick = useCallback(
    (e: MapMouseEvent) => {
      if (!onClick) return;
      const info = extractTrailInfo(e);
      if (info) {
        e.preventDefault();
        onClick(info);
      }
    },
    [extractTrailInfo, onClick],
  );

  // Context menu handler
  const handleContextMenu = useCallback(
    (e: MapMouseEvent) => {
      if (!onContextMenu) return;
      const info = extractTrailInfo(e);
      if (info) {
        e.preventDefault();
        onContextMenu(info);
      }
    },
    [extractTrailInfo, onContextMenu],
  );

  // Attach click handlers to trail layers
  useEffect(() => {
    if (!map || availableLayers.length === 0 || !enabled) return;

    availableLayers.forEach((layerId) => {
      map.on("click", layerId, handleClick);
      map.on("contextmenu", layerId, handleContextMenu);
    });

    return () => {
      availableLayers.forEach((layerId) => {
        map.off("click", layerId, handleClick);
        map.off("contextmenu", layerId, handleContextMenu);
      });
    };
  }, [map, availableLayers, enabled, handleClick, handleContextMenu]);

  // Cursor feedback on hover
  useEffect(() => {
    if (!map || availableLayers.length === 0 || !enabled) return;

    const handleEnter = () => {
      map.getCanvas().style.cursor = "pointer";
    };

    const handleLeave = () => {
      map.getCanvas().style.cursor = "";
    };

    availableLayers.forEach((layerId) => {
      map.on("mouseenter", layerId, handleEnter);
      map.on("mouseleave", layerId, handleLeave);
    });

    return () => {
      availableLayers.forEach((layerId) => {
        map.off("mouseenter", layerId, handleEnter);
        map.off("mouseleave", layerId, handleLeave);
      });
    };
  }, [map, availableLayers, enabled]);

  // Virtual layer - nothing to render
  return null;
}
